
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import axios from '@/plugins/request';
import PickTicketPrintTemplate from '@/components/PickTicketPrintTemplateOptimized.vue';

@Component({
  components: {
    PickTicketPrintTemplate,
  },
})
export default class AutoPrintPickTicket extends Vue {
  @Prop({ type: Number, required: true }) pickTicketId!: number;
  @Prop({ type: String, required: true }) printToken!: string;
  @Prop({ type: String, default: null }) printedBy!: string | null;

  loading = true;
  loadingOrder = false;
  validated = false;
  error = '';
  orderId: number | null = null;
  printReady = false;

  async mounted() {
    await this.validateToken();
  }

  async validateToken() {
    this.loading = true;
    this.error = '';
    this.validated = false;

    if (!this.printToken) {
      this.error = 'No print token provided';
      this.loading = false;
      return;
    }

    try {
      const response = await axios.post('/api/print/validate-token', {
        token: this.printToken,
        documentType: 'PickTicket',
        documentId: this.pickTicketId,
      });

      if (response.data.valid) {
        console.log('[AutoPrintPickTicket] Token validated for pick ticket:', this.pickTicketId);

        // Always bind the store reference — required on every fresh page load
        // because $systemCategories is a fresh module-level object each time (#1264).
        this.$systemCategories.load(this);

        // Always fetch fresh system categories to avoid stale localStorage data
        await this.$store.dispatch('retrievesystemCategories', this);

        this.validated = true;
        // Now fetch the order ID needed for the print template
        await this.fetchOrderId();
      } else {
        this.error = response.data.reason || 'Invalid or expired print token';
        console.error('[AutoPrintPickTicket] Token validation failed:', response.data.reason);
      }
    } catch (err: any) {
      console.error('[AutoPrintPickTicket] Token validation error:', err);

      if (err.response?.status === 404) {
        this.error = 'Print token not found or already used';
      } else if (err.response?.data?.reason) {
        this.error = err.response.data.reason;
      } else {
        this.error = 'Failed to validate print token';
      }
    } finally {
      this.loading = false;
    }
  }

  async fetchOrderId() {
    this.loadingOrder = true;

    try {
      // Fetch the print data to get the orderId
      // The PickTicketPrintTemplate will fetch its own detailed data
      const response = await axios.get(`/api/shipper/pick-ticket/${this.pickTicketId}/print-data`);

      this.orderId = response.data.order?.salesOrderId;

      console.log('[AutoPrintPickTicket] Fetched orderId:', this.orderId, 'for pickTicketId:', this.pickTicketId);

      if (!this.orderId) {
        console.error('[AutoPrintPickTicket] No orderId found in response:', response.data);
        this.error = 'Unable to determine order ID from pick ticket data';
      } else {
        // Start watching for print template loading state
        this.$nextTick(() => this.watchPrintTemplateLoading());
      }
    } catch (err: any) {
      console.error('[AutoPrintPickTicket] Failed to fetch order ID:', err);

      if (err.response?.status === 404) {
        this.error = 'Pick ticket not found';
      } else if (err.response?.status === 403) {
        this.error = 'Access denied to pick ticket data';
      } else {
        this.error = err.response?.data?.message || 'Failed to load pick ticket data';
      }
    } finally {
      this.loadingOrder = false;
    }
  }

  watchPrintTemplateLoading() {
    // Poll the print template's loading state
    // Once template.loading is false, the iframes are created
    // Then we wait for the pagedjs-complete event from each iframe
    const checkLoading = () => {
      const template = this.$refs.printTemplate as any;
      if (template && !template.loading) {
        console.log('[AutoPrintPickTicket] Template loaded, waiting for PagedJS completion...');
        // Fallback in case PagedJS completion event doesn't fire (e.g., render error).
        // Normal completion is event-driven via onPagedJsComplete and typically fires
        // in <5s. This 25s fallback accommodates complex pick tickets with many pages
        // that may legitimately take 20+ seconds to render. Kept below Puppeteer's
        // PageRenderTimeoutSeconds (45s) so Vue signals ready first even if PagedJS
        // genuinely hangs.
        setTimeout(() => {
          if (!this.printReady) {
            console.warn('[AutoPrintPickTicket] PagedJS completion timeout, setting ready anyway');
            this.printReady = true;
          }
        }, 25000); // 25 second fallback timeout
      } else if (!this.printReady) {
        setTimeout(checkLoading, 500);
      }
    };
    checkLoading();
  }

  onPagedJsComplete() {
    console.log('[AutoPrintPickTicket] PagedJS complete event received, print ready');
    this.printReady = true;
  }

  @Watch('pickTicketId')
  onPickTicketIdChange() {
    this.printReady = false;
    this.validateToken();
  }
}
